// WebAuthn (passkey) browser helpers.
//
// The backend speaks base64url strings (the WebAuthn JSON encoding used by
// @simplewebauthn/server); navigator.credentials speaks ArrayBuffers. These
// pure functions translate between the two. No state lives here.

import { isNativeApp, hasNativePasskeys } from './nativeApp';

// Every passkey button and the store guard go through this one check. Inside the
// store apps the API can exist and still be unusable (WKWebView without an
// associated domain, Android WebView), so a native page must also have declared
// support — see hasNativePasskeys. On the web only the API presence counts.
export function isSupported(win = typeof window !== 'undefined' ? window : undefined) {
    if (!win) {
        return false;
    }
    const credentials = win.navigator && win.navigator.credentials;
    const hasApi = !!win.PublicKeyCredential && !!(credentials && credentials.create && credentials.get);
    if (!hasApi) {
        return false;
    }
    if (isNativeApp(win) && !hasNativePasskeys(win)) {
        return false;
    }
    return true;
}

export function bufferToBase64url(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export function base64urlToBuffer(base64url) {
    const base64 = String(base64url).replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

export function utf8ToBuffer(str) {
    const utf8 = encodeURIComponent(str)
        .replace(/%([0-9A-F]{2})/gi, (match, hex) => String.fromCharCode(parseInt(hex, 16)));
    const bytes = new Uint8Array(utf8.length);
    for (let i = 0; i < utf8.length; i += 1) {
        bytes[i] = utf8.charCodeAt(i);
    }
    return bytes.buffer;
}

export function toCreationOptions(optionsJSON) {
    return {
        ...optionsJSON,
        challenge: base64urlToBuffer(optionsJSON.challenge),
        user: {
            ...optionsJSON.user,
            // @simplewebauthn/server v9 sends user.id as the RAW userID
            // string (unlike challenge/credential ids, which are base64url).
            // It must be UTF-8 encoded — atob() throws on ids like "2".
            id: utf8ToBuffer(optionsJSON.user.id),
        },
        excludeCredentials: (optionsJSON.excludeCredentials || []).map(cred => ({
            ...cred,
            id: base64urlToBuffer(cred.id),
        })),
    };
}

export function toRequestOptions(optionsJSON) {
    return {
        ...optionsJSON,
        challenge: base64urlToBuffer(optionsJSON.challenge),
        allowCredentials: optionsJSON.allowCredentials
            ? optionsJSON.allowCredentials.map(cred => ({
                ...cred,
                id: base64urlToBuffer(cred.id),
            }))
            : undefined,
    };
}

export function credentialToJSON(credential) {
    const { response } = credential;
    return {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        authenticatorAttachment: credential.authenticatorAttachment || undefined,
        response: {
            clientDataJSON: bufferToBase64url(response.clientDataJSON),
            attestationObject: bufferToBase64url(response.attestationObject),
            transports: typeof response.getTransports === 'function' ? response.getTransports() : [],
        },
        clientExtensionResults: typeof credential.getClientExtensionResults === 'function'
            ? credential.getClientExtensionResults()
            : {},
    };
}

export function assertionToJSON(credential) {
    const { response } = credential;
    return {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        authenticatorAttachment: credential.authenticatorAttachment || undefined,
        response: {
            clientDataJSON: bufferToBase64url(response.clientDataJSON),
            authenticatorData: bufferToBase64url(response.authenticatorData),
            signature: bufferToBase64url(response.signature),
            userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : undefined,
        },
        clientExtensionResults: typeof credential.getClientExtensionResults === 'function'
            ? credential.getClientExtensionResults()
            : {},
    };
}

// Runs the full browser side of a registration ceremony.
export async function createCredential(optionsJSON) {
    const credential = await navigator.credentials.create({
        publicKey: toCreationOptions(optionsJSON),
    });
    return credentialToJSON(credential);
}

// Runs the full browser side of a login ceremony.
export async function getAssertion(optionsJSON) {
    const credential = await navigator.credentials.get({
        publicKey: toRequestOptions(optionsJSON),
    });
    return assertionToJSON(credential);
}
